import type React from "react"
import { Col, Row } from "reactstrap"
import {
  Line,
  LineChart,
  Bar,
  BarChart,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts"
import type { DashboardChartsData } from "./types/dashboard"
import "../../CommonElements/DashboardTheme/dashboard-theme.css"

interface DetailedChartsProps {
  chartsData: DashboardChartsData | null
  loading: boolean
}

const DetailedCharts: React.FC<DetailedChartsProps> = ({ chartsData, loading }) => {
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-3 py-2 border-0 rounded shadow-sm" style={{ fontSize: "0.82rem" }}>
          <p className="text-muted mb-2">{new Date(label).toLocaleDateString()}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="mb-1" style={{ color: entry.color }}>
              <strong>{entry.name}:</strong> {entry.value}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  if (loading) {
    return (
      <Row>
        {Array.from({ length: 2 }).map((_, index) => (
          <Col xl="12" lg="12" className="mb-4" key={index}>
            <div className="db-chart-card">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="db-skeleton" style={{ height: "22px", width: "35%" }}></div>
                <div className="db-skeleton" style={{ width: "36px", height: "36px", borderRadius: "8px" }}></div>
              </div>
              <div className="db-skeleton" style={{ height: "320px", width: "100%" }}></div>
            </div>
          </Col>
        ))}
      </Row>
    )
  }

  return (
    <Row>
      <Col xl="12" lg="12" className="mb-4">
        <div className="db-chart-card">
          <div className="db-chart-head">
            <div>
              <h3 className="db-chart-title">Ticket Activity Trends</h3>
              <p className="db-chart-sub">Daily ticket purchases and usage over time</p>
            </div>
            <div className="db-kpi-chip db-accent-indigo">
              <i className="fa fa-bar-chart"></i>
            </div>
          </div>
          <div style={{ height: "350px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartsData?.ticketTrends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value: string) =>
                    new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  }
                  stroke="#98a2b3"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#98a2b3" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(79,70,229,0.05)" }} />
                <Legend wrapperStyle={{ fontSize: "0.82rem" }} />
                <Bar dataKey="newBought" fill="#4f46e5" name="New Bought" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="used" fill="#059669" name="Used" radius={[4, 4, 0, 0]} maxBarSize={28} />
                <Bar dataKey="unused" fill="#d97706" name="Unused" radius={[4, 4, 0, 0]} maxBarSize={28} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      <Col xl="12" lg="12" className="mb-4">
        <div className="db-chart-card">
          <div className="db-chart-head">
            <div>
              <h3 className="db-chart-title">Vehicle Traffic Flow</h3>
              <p className="db-chart-sub">Daily vehicle entries and exits</p>
            </div>
            <div className="db-kpi-chip db-accent-emerald">
              <i className="fa fa-line-chart"></i>
            </div>
          </div>
          <div style={{ height: "350px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartsData?.vehicleTrends || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value: string) =>
                    new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  }
                  stroke="#98a2b3"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#98a2b3" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ fontSize: "0.82rem" }} />
                <Line
                  type="monotone"
                  dataKey="entered"
                  stroke="#4f46e5"
                  strokeWidth={3}
                  name="Entries"
                  dot={{ fill: "#4f46e5", r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="exited"
                  stroke="#e11d48"
                  strokeWidth={3}
                  name="Exits"
                  dot={{ fill: "#e11d48", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>

      <Col xl="12" className="mb-4">
        <div className="db-chart-card">
          <div className="db-chart-head">
            <div>
              <h3 className="db-chart-title">Badge Verification Activity</h3>
              <p className="db-chart-sub">Daily badge verification entries at turnstiles</p>
            </div>
            <div className="db-kpi-chip db-accent-cyan">
              <i className="fa fa-shield"></i>
            </div>
          </div>
          <div style={{ height: "300px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartsData?.badgeVerifications || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value: string) =>
                    new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  }
                  stroke="#98a2b3"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 12 }}
                />
                <YAxis stroke="#98a2b3" tickLine={false} axisLine={false} tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="verifications"
                  stroke="#0891b2"
                  strokeWidth={3}
                  name="Verifications"
                  dot={{ fill: "#0891b2", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </Col>
    </Row>
  )
}

export default DetailedCharts
